import lodashSet from 'lodash.set';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/redux/store';
import { questionnairesApi } from 'app/services/questionnaires';
import { resetQuestionnaire } from '../../../actions';

type TYesOrNo = 'yes' | 'no' | null;

export type TSpouseCitizenships = TSpouseCitizenshipsRow[];

export type TSpouseCitizenshipsRow = {
  country: Nullable<string>;
};

export interface FamilyStateValues {
  fillingWithSpouse: TYesOrNo | 'notSure';
  spouseFirstName: Nullable<string>;
  spouseMiddleName: Nullable<string>;
  spouseLastName: Nullable<string>;
  spouseDateOfBirth: Nullable<string>;
  spouseOccupation: Nullable<string>;
  isSpouseUsCitizen: TYesOrNo;
  isSpouseGcHolder: TYesOrNo;
  confirmSpouseSeperateUsTaxReturn: Nullable<string>;
  didSpouseRenounceGcDuringYear: TYesOrNo;
  GcCopy: Nullable<string>;
  spouseHasSocialSecurityNum: TYesOrNo;
  spouseSocialSecurityNum: Nullable<string>;
  spouseCitizenships: TSpouseCitizenships;
  marriedForPartOfFillingYear: TYesOrNo;
  marriedForPartOfFillingYearDetails: Nullable<string>;
  IrsSentIdentityProtection: TYesOrNo;
  pleaseEnterPin: Nullable<string>;
}

export interface FamilyState {
  values: FamilyStateValues;
}

const initialState: FamilyState = {
  values: {
    fillingWithSpouse: null,
    spouseFirstName: null,
    spouseMiddleName: null,
    spouseLastName: null,
    spouseDateOfBirth: null,
    spouseOccupation: null,
    isSpouseUsCitizen: null,
    isSpouseGcHolder: null,
    confirmSpouseSeperateUsTaxReturn: null,
    didSpouseRenounceGcDuringYear: null,
    GcCopy: null,
    spouseHasSocialSecurityNum: null,
    spouseSocialSecurityNum: null,
    spouseCitizenships: [{ country: null }],
    marriedForPartOfFillingYear: null,
    marriedForPartOfFillingYearDetails: null,
    IrsSentIdentityProtection: null,
    pleaseEnterPin: null,
  },
};

const familySlice = createSlice({
  name: 'family',
  initialState,
  reducers: {
    update(state, action: PayloadAction<FamilyStateValues>) {
      state.values = JSON.parse(JSON.stringify(action.payload));
    },
    updateKey(
      state,
      action: PayloadAction<{
        key: keyof FamilyStateValues | keyof TSpouseCitizenshipsRow;
        value: any;
      }>,
    ) {
      lodashSet(state, `values.${action.payload.key}`, action.payload.value);
    },
    updateFieldArrayKey(
      state,
      action: PayloadAction<{
        key: keyof FamilyStateValues | keyof TSpouseCitizenshipsRow;
        value: TSpouseCitizenships;
      }>,
    ) {
      const fieldArray = action.payload.value.map((field) => ({ ...field }));
      if (action.payload.key === 'spouseCitizenships') {
        state.values[action.payload.key] = fieldArray;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetQuestionnaire, () => initialState);

    builder.addMatcher(
      questionnairesApi.endpoints.getQuestionnaireById.matchFulfilled,
      (state, { payload }) => {
        state.values = payload.questionnaire.data.taxQuestionnaire
          ? payload.questionnaire.data.taxQuestionnaire.personal_details
              .the_basics.family.values
          : initialState.values;
      },
    );
  },
});

export const { update, updateKey, updateFieldArrayKey } = familySlice.actions;
export default familySlice.reducer;

export const selectFamily = (state: RootState) =>
  state.taxQuestionnaire.personal_details.the_basics.family;
